import PropTypes from "prop-types";
import { sendTagOnShowMapClicked, sendTagOnShowListClicked } from "app/utils/analytics";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeviceType, useIntersectionObserver } from "app/hooks";
import { ActionBarMobile } from "./components/mobile/ActionBarMobile";
import { ActionBarDesktop } from "./components/desktop/ActionBarDesktop";

const OBSERVER_OPTIONS = {
	rootMargin: "1px 0px 0px 0px",
};

export const ActionBar = ({
	isSortAvailable = true,
	showMapButton = false,
	isLoading = true,
	onSubmit,
	onSortOpen,
	onFilterOpen,
	onApplyFilters,
	onSortChange,
	onFilterChange,
	onFilterTriggerMobileOpen,
	observerOptions = OBSERVER_OPTIONS,
	...props
}) => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const [sentinelRef, inView] = useIntersectionObserver(observerOptions);
	const { isDesktop } = useDeviceType();

	const isSticky = !inView;

	const isMapPage = pathname.includes("/map");

	const handleToggleMap = () => {
		if (isMapPage) {
			navigate("../" + search, { replace: true });
			sendTagOnShowListClicked();
		} else {
			navigate({ pathname: "./map", search }, { replace: true });
			sendTagOnShowMapClicked();
		}
	};

	const handleFilterOpen = filterId => {
		if (typeof onFilterOpen === "function") {
			onFilterOpen(filterId, isSticky);
		}
	};

	const handleSortOpen = () => {
		if (typeof onSortOpen === "function") {
			onSortOpen(isSticky);
		}
	};

	const handleFilterTriggerMobileOpen = () => {
		if (typeof onFilterTriggerMobileOpen === "function") {
			onFilterTriggerMobileOpen(isSticky);
		}
	};

	const handleSortChange = sortById => {
		if (typeof onSortChange === "function") {
			onSortChange(sortById);
		}

		if (typeof onSubmit === "function") {
			onSubmit(sortById);
		}
	};

	const handleApplyFilters = (selectedFilters, resultsCount) => {
		if (typeof onApplyFilters === "function") {
			onApplyFilters(selectedFilters, resultsCount);
		}
		if (typeof onSubmit === "function") {
			onSubmit(selectedFilters, resultsCount);
		}
	};

	return (
		<>
			<div className="action-bar__sentinel" ref={sentinelRef} aria-hidden="true" />

			{isDesktop ? (
				<ActionBarDesktop
					showMapButton={showMapButton}
					isSortAvailable={isSortAvailable}
					isSticky={isSticky}
					isLoading={isLoading}
					onFilterOpen={handleFilterOpen}
					onSortOpen={handleSortOpen}
					onSortSubmit={handleSortChange}
					onFilterSubmit={handleApplyFilters}
					onMapClick={handleToggleMap}
					onSubmit={onSubmit}
					{...props}
				/>
			) : (
				<ActionBarMobile
					showMapButton={showMapButton}
					isSortAvailable={isSortAvailable}
					isSticky={isSticky}
					isLoading={isLoading}
					onFilterOpen={handleFilterTriggerMobileOpen}
					onFilterPanelOpen={handleFilterOpen}
					onSortOpen={handleSortOpen}
					onSortChange={handleSortChange}
					onApplyFilters={handleApplyFilters}
					onFilterChange={onFilterChange}
					onMapClick={handleToggleMap}
					{...props}
				/>
			)}
		</>
	);
};

ActionBar.propTypes = {
	isSortAvailable: PropTypes.bool,
	showMapButton: PropTypes.bool,
	isLoading: PropTypes.bool,
	onSubmit: PropTypes.func,
	onSortOpen: PropTypes.func,
	onFilterOpen: PropTypes.func,
	onSortChange: PropTypes.func,
	onFilterChange: PropTypes.func,
	onApplyFilters: PropTypes.func,
	onFilterTriggerMobileOpen: PropTypes.func,
	observerOptions: PropTypes.object,
	className: PropTypes.string,
};
